import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  RichTextEditor,
  RichTextEditorControl,
  useRichTextEditorContext,
} from '@mantine/tiptap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import {
  getWebLinksSnapshot, loadWebLinks, subscribeWebLinks,
} from '../../../utils/webLinkRegistry';
import { buildWebLinkOptions } from '../../../utils/webLinks';
import DocumentDownloadField from '../../../puck/fields/DocumentDownloadField';
import { createSectionColumnId } from '../../../utils/proposalSectionColumns';
import styles from './RichTextBubbleLinkControls.module.css';

/**
 * Contrôles lien du bubble menu — panneau maison, sans portail.
 *
 * Remplace `RichTextEditor.Link` de Mantine dont le Popover est portalisé
 * vers document.body : le focus dans son input fait blur l'éditeur, le
 * shell croit l'interaction terminée et le bubble menu se démonte, tuant
 * le popover avec lui (même mécanique que le Select des polices, voir
 * RichTextBubbleFontFamilyPicker). Ici le panneau est un enfant React
 * normal du shell : lock du menu à l'ouverture, restore de la sélection
 * à la fermeture.
 */
export default function RichTextBubbleLinkControls({
  editor,
  rememberNow,
  runWithSelection,
  lockBubbleMenu,
  unlockBubbleMenu,
  onRestoreSelection,
}) {
  const ctx = useRichTextEditorContext();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [download, setDownload] = useState(null);
  const [external, setExternal] = useState(true);
  /* Identifiant du lien de banque dont l'adresse a été copiée. Il survit dans
     le HTML (`data-link-id`) et c'est lui qui permet à « Rafraîchir » de
     remettre l'adresse à jour plus tard. Une adresse tapée à la main n'en a
     pas — d'où sa remise à zéro dès que l'utilisateur frappe. */
  const [linkId, setLinkId] = useState(null);
  const [bankOpen, setBankOpen] = useState(false);
  const bank = useSyncExternalStore(subscribeWebLinks, getWebLinksSnapshot);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const bankGroups = useMemo(() => buildWebLinkOptions(bank.groups), [bank.groups]);

  const close = () => {
    setOpen(false);
    setUrl('');
    setLinkId(null);
    setBankOpen(false);
    unlockBubbleMenu?.();
    window.requestAnimationFrame(() => onRestoreSelection?.());
  };

  const openPanel = () => {
    rememberNow?.();
    lockBubbleMenu?.();
    const linkAttrs = editor?.getAttributes('link') || {};
    setUrl(linkAttrs.href || '');
    let form = {};
    try { form = JSON.parse(linkAttrs.downloadForm || '{}'); } catch { form = {}; }
    setDownload(linkAttrs.downloadId ? { kind: 'download', url: linkAttrs.href || '', filename: linkAttrs.download || '', documentId: linkAttrs.documentId, form, downloadId: linkAttrs.downloadId } : null);
    setLinkId(linkAttrs.linkId || null);
    setBankOpen(false);
    // Nouveau lien : « ouvrir dans un nouvel onglet » actif par défaut.
    setExternal(linkAttrs.href ? linkAttrs.target === '_blank' : true);
    setOpen(true);
  };

  // Focus après montage du panneau — le rAF depuis le mousedown du trigger
  // pouvait tirer avant/à côté, laissant le focus (et les frappes) dans
  // l'éditeur : chaque caractère remplaçait alors la sélection.
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [open]);

  /**
   * Même parade que PuckColorField.handleBubbleMouseDown : un mousedown dans
   * le panneau ne doit jamais laisser le focus (re)partir vers l'éditeur —
   * sinon les frappes tapent dans la sélection du texte riche.
   */
  const handlePanelMouseDown = (e) => {
    e.stopPropagation();
    if (e.target instanceof HTMLInputElement) {
      e.preventDefault();
      e.target.focus();
    }
  };

  const handleTriggerMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (open) close();
    else openPanel();
  };

  /**
   * Comme partout dans ce bubble menu (swatches, palette, format, police) :
   * action au mousedown + preventDefault — les `click` n'arrivent jamais
   * jusqu'aux boutons dans cette stack (overlay Puck / bubble menu).
   */
  const handleSaveMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    save();
  };

  /* Chargement paresseux : la banque n'est lue qu'à la première ouverture de
     la liste, jamais à l'ouverture de l'éditeur. */
  const handleBankMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBankOpen((wasOpen) => {
      if (!wasOpen) loadWebLinks();
      return !wasOpen;
    });
  };

  const handleBankPickMouseDown = (e, link) => {
    e.preventDefault();
    e.stopPropagation();
    setUrl(link.url);
    setLinkId(link.value);
    setBankOpen(false);
  };

  const handleExternalMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setExternal((v) => !v);
  };

  const save = () => {
    const href = download ? download.url || '' : url.trim();
    rememberNow?.();
    if (href === '' && !download) {
      runWithSelection((chain) => chain.extendMarkRange('link').unsetLink());
    } else {
      runWithSelection((chain) => chain.extendMarkRange('link').setLink({
        href,
        target: !download && external ? '_blank' : null,
        linkId: !download && linkId ? String(linkId) : null,
        download: download ? download.filename || '' : null,
        downloadId: download?.downloadId || null,
        documentId: download?.documentId ? String(download.documentId) : null,
        downloadForm: download ? JSON.stringify(download.form || {}) : null,
      }));
    }
    close();
  };

  const handleUnlinkMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    rememberNow?.();
    runWithSelection((chain) => chain.extendMarkRange('link').unsetLink());
  };

  const handleInputKeyDown = (e) => {
    // Les frappes ne doivent pas remonter (raccourcis Puck / éditeur).
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      // Le bubble menu re-dispatche un mousedown synthétique ciblant sa
      // racine (cf. handleBubbleMenuPointerDownCapture) : vu comme un clic
      // « extérieur », il fermait le panneau avant le click des boutons.
      if (!e.isTrusted) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    document.addEventListener('mousedown', handleOutside, { capture: true });
    return () => document.removeEventListener('mousedown', handleOutside, { capture: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className={`${styles.wrap} ${open ? styles.wrapOpen : ''}`.trim()} ref={wrapRef}>
      <RichTextEditor.ControlsGroup>
        <RichTextEditorControl
          active={editor?.isActive('link')}
          aria-label={ctx.labels.linkControlLabel}
          onMouseDown={handleTriggerMouseDown}
        >
          <FontAwesomeIcon icon={icon({ name: 'link', family: 'duotone', style: 'solid' })} style={{ fontSize: 16 }} />
        </RichTextEditorControl>
        <RichTextEditorControl
          aria-label={ctx.labels.unlinkControlLabel}
          onMouseDown={handleUnlinkMouseDown}
        >
          <FontAwesomeIcon icon={icon({ name: 'link-slash', family: 'duotone', style: 'solid' })} style={{ fontSize: 16 }} />
        </RichTextEditorControl>
      </RichTextEditor.ControlsGroup>
      {open && (
        <div className={styles.panel} data-bubble-link-panel="" onMouseDown={handlePanelMouseDown}>
          <div className={styles.group}>
            <div className={styles.inputZone}>
              <input
                ref={inputRef}
                type="url"
                disabled={!!download}
                className={styles.input}
                placeholder={ctx.labels.linkEditorInputPlaceholder}
                aria-label={ctx.labels.linkEditorInputLabel}
                value={url}
                onChange={(e) => { setUrl(e.target.value); setLinkId(null); }}
                onKeyDown={handleInputKeyDown}
              />
              <button
                type="button"
                className={`${styles.bankToggle} ${bankOpen ? styles.bankToggleActive : ''}`}
                disabled={!!download}
                title="Banque de liens"
                aria-label="Banque de liens"
                onMouseDown={handleBankMouseDown}
              >
                <FontAwesomeIcon icon={icon({ name: 'bookmark', style: 'light' })} style={{ fontSize: 14 }} />
              </button>
              <button
                type="button"
                className={`${styles.externalToggle} ${external ? styles.externalToggleActive : ''}`}
                disabled={!!download}
                title={external ? ctx.labels.linkEditorExternalLink : ctx.labels.linkEditorInternalLink}
                aria-label={external ? ctx.labels.linkEditorExternalLink : ctx.labels.linkEditorInternalLink}
                onMouseDown={handleExternalMouseDown}
              >
                <FontAwesomeIcon icon={icon({ name: 'arrow-up-right-from-square', style: 'light' })} style={{ fontSize: 14 }} />
              </button>
            </div>
            <button type="button" className={styles.saveButton} onMouseDown={handleSaveMouseDown}>
              {ctx.labels.linkEditorSave}
            </button>
          </div>
          <button type="button" className={styles.downloadToggle} aria-pressed={!!download} onMouseDown={(event) => {
            event.preventDefault(); event.stopPropagation();
            setDownload((current) => current ? null : { kind: 'download', url: '', downloadId: createSectionColumnId(), form: {} });
            setBankOpen(false);
          }}>Télécharger un document</button>
          {download && <DocumentDownloadField key={download.downloadId} value={download} onChange={setDownload} withFormSettings />}
          {bankOpen && (
            <div className={styles.bankList}>
              {bank.status === 'loading' && (
                <div className={styles.bankEmpty}>Chargement…</div>
              )}
              {/* « Vide » et « illisible » ne se disent pas pareil : annoncer
                  « aucun lien » quand la lecture a échoué enverrait
                  l'utilisateur vérifier une banque qui est pleine. */}
              {(bank.status === 'error' || bank.status === 'unavailable') && (
                <div className={styles.bankEmpty}>
                  {bank.status === 'error'
                    ? 'La banque de liens n\'a pas pu être lue.'
                    : 'La banque de liens n\'est pas disponible ici.'}
                </div>
              )}
              {bank.status === 'ready' && bankGroups.length === 0 && (
                <div className={styles.bankEmpty}>Aucun lien dans la banque.</div>
              )}
              {bank.status === 'ready' && bankGroups.map((group) => (
                <div key={group.group}>
                  <div className={styles.bankGroup}>{group.group}</div>
                  {group.items.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      className={styles.bankItem}
                      onMouseDown={(e) => handleBankPickMouseDown(e, item)}
                    >
                      {item.label}
                      <span className={styles.bankItemUrl}>{item.url}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
